import { useAuth } from "@/hooks";
import { supabase } from "@/integrations/supabase/client";
import { RealSchoolSettings, BasicInfoField } from "@/types/realSchoolSettings";
import { getDefaultStages, parseAlertRules } from "@/utils/schoolSettingsUtils";
import { useParams } from "react-router-dom";

// Cache محسن للإعدادات
// let settingsCache: RealSchoolSettings | null = null;

export const schoolSettingsService = {
  async fetchSettings(
    schoolId: string,
    user: any
  ): Promise<RealSchoolSettings | null> {
    // جلب الإعدادات باستخدام school_id الصحيح
    const { data: existingSettings, error: settingsError } = await supabase
      .from("school_settings")
      .select("*")
      .eq("school_id", schoolId)
      .maybeSingle();

    if (settingsError && settingsError.code !== "PGRST116") {
      throw settingsError;
    }

    let finalSettings: RealSchoolSettings;

    // إذا لم توجد إعدادات، إنشاء إعدادات جديدة
    if (!existingSettings) {
      console.error(
        "there is no existing settingins for the school: ",
        schoolId
      );
      // finalSettings = await this.createDefaultSettings(user);
    } else {
      finalSettings = this.processExistingSettings(existingSettings, user);
    }

    // حفظ في الكاش
    // settingsCache = finalSettings;

    return finalSettings;
  },

  async createDefaultSettings(user: any): Promise<RealSchoolSettings> {
    const defaultStages = getDefaultStages();

    const newSettings = {
      school_id: user.id,
      school_name:
        user.user_metadata?.school_name ||
        user.email?.split("@")[0] ||
        "مدرستي",
      logo_url: null,
      stages: defaultStages,
      absence_alert_days: 3,
      late_alert_count: 3,
      absence_alert_title: "تنبيه غياب",
      late_alert_title: "تنبيه تأخر",
      absence_alert_description: "تجاوز الطالب العدد المسموح من أيام الغياب",
      late_alert_description: "تجاوز الطالب العدد المسموح من مرات التأخر",
      alert_rules: null,
    };

    const { data: createdData, error: createError } = await supabase
      .from("school_settings")
      .insert([newSettings])
      .select()
      .single();

    if (createError) {
      throw createError;
    }

    return {
      ...createdData,
      stages: Array.isArray(createdData.stages)
        ? createdData.stages
        : defaultStages,
      basic_info_fields: Array.isArray(createdData.basic_info_fields)
        ? (createdData.basic_info_fields as unknown as BasicInfoField[])
        : createdData.basic_info_fields
        ? JSON.parse(createdData.basic_info_fields as string)
        : [],
      alert_rules: parseAlertRules(createdData.alert_rules),
    };
  },

  processExistingSettings(
    existingSettings: any,
    user: any
  ): RealSchoolSettings {
    // معالجة سريعة للإعدادات الموجودة بدون استعلامات إضافية
    const stages = Array.isArray(existingSettings.stages)
      ? existingSettings.stages
      : [];
    const defaultStages = getDefaultStages();

    // فحص المراحل المفقودة وإضافتها بدون تحديث فوري لقاعدة البيانات
    const existingStageIds = stages.map((stage: any) => stage.id);
    const missingStages = defaultStages.filter(
      (stage) => !existingStageIds.includes(stage.id)
    );

    // if (missingStages.length > 0) {
    //   stages = [...stages, ...missingStages];

    //   // تحديث قاعدة البيانات في الخلفية بدون انتظار
    //   this.updateStagesInBackground(user.id, stages);
    // }

    return {
      ...existingSettings,
      stages: stages,
      basic_info_fields: Array.isArray(existingSettings.basic_info_fields)
        ? (existingSettings.basic_info_fields as unknown as BasicInfoField[])
        : existingSettings.basic_info_fields
        ? JSON.parse(existingSettings.basic_info_fields as string)
        : [],
      alert_rules: parseAlertRules(existingSettings.alert_rules),
    };
  },

  // تحديث المراحل في الخلفية
  async updateStagesInBackground(schoolId: string, stages: any[]) {
    try {
      await supabase
        .from("school_settings")
        .update({ stages: stages })
        .eq("school_id", schoolId);
      // settingsCache = null; // مسح الكاش بعد التحديث
    } catch (error) {
      // تجاهل أخطاء التحديث في الخلفية
    }
  },

  // مسح الكاش
  // clearCache() {
  //   settingsCache = null;
  // },

  // إجبار إعادة تحميل الإعدادات (مسح الكاش وإعادة التحميل)
  async forceRefresh(): Promise<RealSchoolSettings | null> {
    // this.clearCache();
    return await this.fetchSettings();
  },

  async updateSettings(
    updates: Partial<RealSchoolSettings>,
    currentSettings?: RealSchoolSettings | null
  ): Promise<RealSchoolSettings> {
    // مسح الكاش قبل التحديث
    // this.clearCache();

    const settingsData = {
      school_id: updates.school_id,
      school_name: updates.school_name || currentSettings?.school_name || "",
      logo_url: updates.logo_url || currentSettings?.logo_url || null,
      stages: updates.stages || currentSettings?.stages || getDefaultStages(),
      country: updates.country || currentSettings?.country,
      ministry: updates.ministry || currentSettings?.ministry,
      education_department:
        updates.education_department || currentSettings?.education_department,
      educational_supervision:
        updates.educational_supervision ||
        currentSettings?.educational_supervision,
      basic_info_fields: updates.basic_info_fields
        ? JSON.stringify(updates.basic_info_fields)
        : currentSettings?.basic_info_fields
        ? JSON.stringify(currentSettings.basic_info_fields)
        : null,
      absence_alert_days:
        updates.absence_alert_days !== undefined
          ? updates.absence_alert_days
          : currentSettings?.absence_alert_days || 3,
      late_alert_count:
        updates.late_alert_count !== undefined
          ? updates.late_alert_count
          : currentSettings?.late_alert_count || 3,
      absence_alert_title:
        updates.absence_alert_title ||
        currentSettings?.absence_alert_title ||
        "تنبيه غياب",
      late_alert_title:
        updates.late_alert_title ||
        currentSettings?.late_alert_title ||
        "تنبيه تأخر",
      absence_alert_description:
        updates.absence_alert_description ||
        currentSettings?.absence_alert_description ||
        "تجاوز الطالب العدد المسموح من أيام الغياب",
      late_alert_description:
        updates.late_alert_description ||
        currentSettings?.late_alert_description ||
        "تجاوز الطالب العدد المسموح من مرات التأخر",
      alert_rules:
        updates.alert_rules !== undefined
          ? JSON.stringify(updates.alert_rules)
          : currentSettings?.alert_rules
          ? JSON.stringify(currentSettings.alert_rules)
          : null,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from("school_settings")
      .upsert(settingsData, {
        onConflict: "school_id",
      })
      .select()
      .single();

    if (error) {
      console.error("❌ خطأ في حفظ البيانات:", error);
      throw error;
    }

    // إذا تم تحديث اسم المدرسة، نحدث أيضاً الملف الشخصي للمستخدم
    // if (updates.school_name && updates.school_name !== currentSettings?.school_name) {
    //   try {
    //     await supabase
    //       .from('profiles')
    //       .update({
    //         full_name: updates.school_name,
    //         updated_at: new Date().toISOString()
    //       })
    //       .eq('id', user.id);

    //     console.log('✅ تم تحديث الملف الشخصي للمستخدم مع اسم المدرسة الجديد');
    //   } catch (profileError) {
    //     console.warn('⚠️ تعذر تحديث الملف الشخصي:', profileError);
    //     // لا نعيد خطأ هنا لأن تحديث الإعدادات نجح
    //   }
    // }

    const finalData = {
      ...data,
      stages: Array.isArray(data.stages) ? data.stages : getDefaultStages(),
      basic_info_fields: Array.isArray(data.basic_info_fields)
        ? (data.basic_info_fields as unknown as BasicInfoField[])
        : data.basic_info_fields
        ? JSON.parse(data.basic_info_fields as string)
        : [],
      alert_rules: parseAlertRules(data.alert_rules),
    };

    // تحديث الكاش بالبيانات الجديدة
    // settingsCache = finalData;

    return finalData;
  },
};
